import axios from "axios";
import config from "../../../config/config";
import { attachSessionInterceptors } from "../../../public/api/httpClient";
import { filterActiveMasterRows } from "../../memberships/utils/masterLookupUtils";

const api = attachSessionInterceptors(
  axios.create({
    baseURL: config.BASE_URL,
    withCredentials: true,
  }),
);

const getCsrfToken = async () => {
  try {
    const response = await api.get("/csrf-token");
    return response.data.csrfToken;
  } catch (error) {
    console.error("Failed to get CSRF token:", error);
    return null;
  }
};

api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem("jwt_token") || localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.method !== "get") {
    const csrfToken = await getCsrfToken();
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }
  return config;
});

function appendMovementListParams(search, params = {}) {
  if (params.page != null) search.append("page", String(params.page));
  if (params.limit != null) search.append("limit", String(params.limit));
  if (params.name) search.append("name", String(params.name).trim());
  if (params.movement_code) search.append("movement_code", String(params.movement_code).trim());
  if (params.movement_date) search.append("movement_date", String(params.movement_date).trim());
  if (params.designation) search.append("designation", String(params.designation).trim());
  if (params.purpose_id != null && params.purpose_id !== "")
    search.append("purpose_id", String(params.purpose_id));
  if (params.status_id != null && params.status_id !== "")
    search.append("status_id", String(params.status_id));
  if (params.place_visited) search.append("place_visited", String(params.place_visited).trim());
  if (params.out_time_contains)
    search.append("out_time_contains", String(params.out_time_contains).trim());
  if (params.in_time_contains)
    search.append("in_time_contains", String(params.in_time_contains).trim());
  if (params.created_by_user_id != null && params.created_by_user_id !== "")
    search.append("created_by_user_id", String(params.created_by_user_id).trim());
}

export const fetchToAndFroMovements = async (params = {}) => {
  try {
    const search = new URLSearchParams();
    appendMovementListParams(search, params);
    const q = search.toString();
    const response = await api.get(`/to-and-fro-register${q ? `?${q}` : ""}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch movements");
  }
};

export const fetchToAndFroStats = async () => {
  try {
    const response = await api.get("/to-and-fro-register/stats");
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch stats");
  }
};

export const fetchToAndFroMasters = async () => {
  try {
    const response = await api.get("/to-and-fro-register/masters");
    const payload = response.data;
    const raw = payload?.data ?? payload;
    return {
      ...payload,
      data: {
        ...raw,
        purposes: filterActiveMasterRows(raw?.purposes),
      },
    };
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch masters");
  }
};

/** Distinct directory filter values from `tbl_to_fro_register` (scoped for office users). */
export const fetchToAndFroFilterOptions = async (column) => {
  try {
    const c = String(column || "").trim();
    const response = await api.get("/to-and-fro-register/filter-options", {
      params: { column: c },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to load filter options");
  }
};

export const getToAndFroMovementById = async (id) => {
  try {
    const response = await api.get(`/to-and-fro-register/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch movement");
  }
};

export const createToAndFroMovement = async (data) => {
  try {
    const response = await api.post("/to-and-fro-register", data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to create movement");
  }
};

export const updateToAndFroMovement = async (id, data) => {
  try {
    const response = await api.put(`/to-and-fro-register/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to update movement");
  }
};

export const returnToAndFroMovement = async (id, data) => {
  try {
    const response = await api.put(`/to-and-fro-register/${id}/return`, data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to return movement");
  }
};

export const deleteToAndFroMovement = async (id) => {
  try {
    const response = await api.delete(`/to-and-fro-register/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to delete movement");
  }
};

export const exportToAndFroReport = async (format = "pdf", params = {}) => {
  try {
    const search = new URLSearchParams();
    appendMovementListParams(search, params);
    const q = search.toString();
    const response = await api.get(
      `/to-and-fro-register/export/${format}${q ? `?${q}` : ""}`,
      { responseType: "blob" },
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `to-and-fro-report.${format === "pdf" ? "pdf" : "csv"}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to export report");
  }
};

/** Staff / volunteer search for movement forms (requires to-and-fro VIEW). */
export const searchToAndFroStaff = async (query) => {
  const params = new URLSearchParams();
  if (query) params.append("q", String(query).trim());
  const response = await api.get(`/to-and-fro-register/staff/search?${params.toString()}`);
  const data = response.data;
  if (response.status < 200 || response.status >= 300) {
    throw new Error(data?.message || "Error searching staff");
  }
  return data;
};
